import {useEffect, useState} from 'react';

import * as Analytics from '../../services/analytics/productEvents';
import {reportCardSnapshotTiming} from './timing';
import {type PointCloudViewerProps} from './types';

type UsePointCloudCardScreenshotArgs = Pick<
  PointCloudViewerProps,
  | 'scene'
  | 'width'
  | 'height'
  | 'renderer'
  | 'sourcePointCount'
  | 'backgroundColor'
  | 'pointColor'
  | 'useRightHandedSystem'
  | 'cameraControl'
  | 'onTiming'
  | 'trackSuccess'
  | 'trackError'
>;

type PointCloudCardScreenshot = {
  screenshot: string | undefined;
  renderErrorMessage: string | undefined;
};

/**
 * Asks the plugged-in renderer for a card screenshot. On success, reports
 * `pointCloud.snapshotRender` and fires the existing preview product event.
 */
export function usePointCloudCardScreenshot({
  scene,
  width,
  height,
  renderer,
  sourcePointCount,
  backgroundColor,
  pointColor,
  useRightHandedSystem,
  cameraControl,
  onTiming,
  trackSuccess,
  trackError,
}: UsePointCloudCardScreenshotArgs): PointCloudCardScreenshot {
  const [screenshot, setScreenshot] = useState<string>();
  const [renderErrorMessage, setRenderErrorMessage] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | undefined;

    const renderCard = async () => {
      const startedAt = performance.now();
      try {
        const result = await renderer.renderCard({
          scene,
          width,
          height,
          backgroundColor,
          pointColor,
          useRightHandedSystem,
          cameraControl,
        });
        if (cancelled) {
          result.cleanup?.();
          return;
        }
        cleanup = result.cleanup;
        reportCardSnapshotTiming(onTiming, {
          renderer: renderer.id,
          durationMs: performance.now() - startedAt,
          sourcePointCount,
        });
        setScreenshot(result.imageUrl);
        setRenderErrorMessage(undefined);
        Analytics.pointCloudViewed({type: 'preview'});
        trackSuccess?.();
      } catch (e) {
        if (cancelled) {
          return;
        }
        setRenderErrorMessage(e instanceof Error ? e.message : 'unknown error');
        trackError?.();
      }
    };

    void renderCard();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [
    scene,
    width,
    height,
    renderer,
    sourcePointCount,
    backgroundColor,
    pointColor,
    useRightHandedSystem,
    cameraControl,
    onTiming,
    trackSuccess,
    trackError,
  ]);

  return {screenshot, renderErrorMessage};
}
